import { Check, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { OrderTaskCargoItem, TaskTimeModeValue } from '@my/tms-api';
import {
  useCreateOrderTaskCargoItem,
  useDeleteOrderTask,
  useDeleteOrderTaskCargoItem,
  useUpdateOrderTask,
  useUpdateOrderTaskCargoItem,
} from '@my/tms-api/hooks';
import { ConfirmDialog, TextInput, showToast } from '@my/ui';
import {
  EMPTY_CARGO_ITEM_DRAFT,
  cargoHandlingPhaseFor,
  cargoItemKindFor,
  cargoItemRowCountFor,
  fromOrderTaskCargoItem,
  resolveLoadingMetres,
  taskTypeOwnsCargoItems,
  taskTypeSupportsReference,
} from '../lib/cargoPick';
import {
  allocationKeyOf,
  deliveredAmount,
  deliveryUnitSlot,
  deriveDeliveryItem,
  type AllocationMap,
} from '../lib/allocation';
import { productLinesEqual, toProductInputs } from '../lib/cargoProducts';
import {
  UNIT_IDENTIFIER_FAMILIES,
  cargoItemUpdateBody,
  cascadeTargets,
  changedUnitIdentifiers,
  unitDraftForRow,
  unitIdentifierLabel,
  type CascadeTarget,
} from '../lib/cargoCascade';
import { AllocationTable } from './AllocationTable';
import { formatTempSpecRange, temperatureRangeViolation } from '../lib/format';
import { TASK_TYPE_CONFIG, taskTypeLabel } from '../lib/taskTypeConfig';
import type {
  CargoItemDraft,
  CollectionItemOption,
  MockTask,
  TaskPanelContext,
  TimeMode,
} from '../lib/types';
import { TaskLocationMap } from './BookingMap';
import { CargoItemInputsSection } from './CargoItemRow';
import { NewLocationButton } from '@/features/locations/NewLocationModal';
import {
  Combobox,
  InputWithSpecHint,
  NumberFieldInput,
  TextAreaInput,
  QuarterHourTimeInput,
  TimeModePicker,
  type ComboboxOption,
} from './inputs';

export function PickupDeliveryForm({
  task,
  panelContext,
  onClose,
}: {
  task: MockTask;
  panelContext: TaskPanelContext;
  /** Collapse the accordion row — Cancel discards edits by unmounting. */
  onClose: () => void;
}) {
  const isPickup = task.type === 'PickUp';
  const source = task._source;
  const updateMutation = useUpdateOrderTask();
  const deleteMutation = useDeleteOrderTask();
  const updateCargoItemMutation = useUpdateOrderTaskCargoItem();
  const createCargoItemMutation = useCreateOrderTaskCargoItem();
  const deleteCargoItemMutation = useDeleteOrderTaskCargoItem();

  // Delete confirmation is gated through a dialog so a stray click
  // on the trash can't drop a planned leg. `pendingDelete` mirrors
  // the document panel's pattern — non-null means "dialog open".
  const [pendingDelete, setPendingDelete] = useState(false);

  // Wire-format initial values used to seed controlled state + to
  // round-trip unchanged columns on save. Pulled off `_source`
  // rather than the adapter's display fields because the BE wants
  // its own enum strings (`Any` not `Undefined` for time mode).
  const initialNotes = source?.notes ?? '';
  const initialPlannedDate = source?.plannedDate ?? '';
  const initialPlannedTime = source?.plannedTime ?? '';
  const initialTimeMode: TaskTimeModeValue = source?.timeMode ?? 'Any';
  const initialAddressId = source?.addressId ?? '';
  const initialTaskReference = source?.taskReference ?? '';
  const initialTemperatureC = source?.temperatureC ?? null;
  /**
   * Cargo-item drafts seeded for the expanded form. Container / tank
   * tasks always render `perTrailer` rows (1 or 2 per physical unit);
   * pallet tasks render at least one. Existing items are placed at
   * their `itemIndex` slot so the row order matches the BE; gaps
   * (e.g. the task carries a slot-1 item but no slot-0 item) are
   * filled with empty drafts so the operator sees the same column
   * shape they got on the create form.
   *
   * <p>
   * If the task carries MORE existing items than the target row
   * count (e.g. a legacy 2-unit container task on a now-1PT variant),
   * the trailing items still render so the operator can see / clear
   * them — same defensive expansion as the order-reference-values
   * helper does for legacy reference data.
   * </p>
   */
  const initialCargoItems = useMemo<CargoItemDraft[]>(() => {
    const existing = source?.cargoItems ?? [];
    const targetCount = cargoItemRowCountFor(
      panelContext.cargoFamily,
      panelContext.perTrailer,
    );

    // A unit-family DROP-OFF shows one unit row per physical unit, not per
    // delivery row: taking two products off one tank makes two delivery rows
    // but it is still one tank. Each slot is hydrated from the first delivery
    // row drawn from that collection unit (legacy unsourced rows by their own
    // itemIndex); its Discharge shows everything that slot delivers.
    if (
      cargoHandlingPhaseFor(source?.type ?? 'PickUp') === 'delivery' &&
      UNIT_IDENTIFIER_FAMILIES.has(panelContext.cargoFamily)
    ) {
      const slotOf = (item: OrderTaskCargoItem) =>
        deliveryUnitSlot(item, panelContext.collectionItems, targetCount);
      const slots: CargoItemDraft[] = [];
      for (let slot = 1; slot <= targetCount; slot++) {
        const rows = existing.filter((i) => slotOf(i) === slot);
        const first = rows[0];
        if (!first) {
          slots.push({ ...EMPTY_CARGO_ITEM_DRAFT, itemIndex: slot });
          continue;
        }
        const weights = rows.map((r) => r.weightKg).filter((w): w is number => w != null);
        slots.push({
          ...fromOrderTaskCargoItem(first),
          itemIndex: slot,
          weightKg: weights.length ? weights.reduce((s, w) => s + w, 0) : null,
        });
      }
      return slots;
    }

    // BE `itemIndex` is 1-based (validator: > 0). The lookup map +
    // the loop below both work in 1-based item-index space; the
    // resulting `drafts` array is still 0-based in JS (drafts[0] is
    // the first slot, with itemIndex = 1).
    const byItemIndex = new Map<number, OrderTaskCargoItem>();
    let maxItemIndex = targetCount;
    for (const item of existing) {
      byItemIndex.set(item.itemIndex, item);
      if (item.itemIndex > maxItemIndex) maxItemIndex = item.itemIndex;
    }
    const drafts: CargoItemDraft[] = [];
    for (let itemIndex = 1; itemIndex <= maxItemIndex; itemIndex++) {
      const item = byItemIndex.get(itemIndex);
      drafts.push(
        item
          ? fromOrderTaskCargoItem(item)
          : { ...EMPTY_CARGO_ITEM_DRAFT, itemIndex },
      );
    }
    return drafts;
  }, [source, panelContext.cargoFamily, panelContext.perTrailer, panelContext.collectionItems]);

  // Controlled state. Seeded once per task because the accordion
  // unmount-remounts this component on selection change — operator
  // edits don't have to survive collapsing then re-expanding the
  // same row.
  const [notes, setNotes] = useState<string>(initialNotes);
  const [plannedDate, setPlannedDate] = useState<string>(initialPlannedDate);
  const [plannedTime, setPlannedTime] = useState<string>(initialPlannedTime);
  const [timeMode, setTimeMode] = useState<TaskTimeModeValue>(initialTimeMode);
  const [addressId, setAddressId] = useState<string>(initialAddressId);
  const [taskReference, setTaskReference] = useState<string>(initialTaskReference);
  const [temperatureC, setTemperatureC] = useState<number | null>(initialTemperatureC);
  const [cargoItems, setCargoItems] = useState<CargoItemDraft[]>(initialCargoItems);

  // ── Delivery allocation (delivery legs only) ───────────────────────────
  // Delivery legs don't free-enter cargo — they draw quantities from the
  // order's collection items. Seed the allocation map from this task's
  // existing sourced items so editing pre-fills what's already delivered.
  const isDelivery = cargoHandlingPhaseFor(source?.type ?? 'PickUp') === 'delivery';
  // Declared here rather than beside the other cargo flags below because
  // `isDirty` reads it, and that runs before them.
  const isUnitFamily = UNIT_IDENTIFIER_FAMILIES.has(panelContext.cargoFamily);
  const initialAllocations = useMemo<AllocationMap>(() => {
    // In each source's measure: a count off pallet lines, kg / litres off a tank.
    const measureBySource = new Map(panelContext.collectionItems.map((c) => [c.id, c.measure]));
    const map: AllocationMap = {};
    for (const item of source?.cargoItems ?? []) {
      const key = allocationKeyOf(item);
      const measure = key ? measureBySource.get(key) : undefined;
      if (!key || !measure) continue;
      map[key] = (map[key] ?? 0) + deliveredAmount(item, measure);
    }
    return map;
  }, [source, panelContext.collectionItems]);
  const [allocations, setAllocations] = useState<AllocationMap>(initialAllocations);
  // Max this delivery may take per collection item = collected − delivered by
  // OTHER legs (this task's own current take is added back in).
  const availableById = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const c of panelContext.collectionItems) {
      const elsewhere =
        (panelContext.deliveredByCollectionItem[c.id] ?? 0) - (initialAllocations[c.id] ?? 0);
      out[c.id] = Math.max(0, c.collectedQty - elsewhere);
    }
    return out;
  }, [panelContext.collectionItems, panelContext.deliveredByCollectionItem, initialAllocations]);
  const isAllocationsDirty = useMemo(() => {
    const keys = new Set([...Object.keys(allocations), ...Object.keys(initialAllocations)]);
    for (const k of keys) {
      if ((allocations[k] ?? 0) !== (initialAllocations[k] ?? 0)) return true;
    }
    return false;
  }, [allocations, initialAllocations]);
  // Per-task tag selection. Hydration priority:
  //   1. The task's own `serviceTagIds` if present (the BE now
  //      carries the per-task selection separately from the order
  //      level).
  //   2. The order's active set, as a fallback for legacy tasks
  //      created before the per-task column landed.
  // The operator's edits on the chip row diff-replace this set on
  // the next save via `useUpdateOrderTask`.
  const initialTagIds: string[] =
    source?.serviceTagIds && source.serviceTagIds.length > 0
      ? source.serviceTagIds
      : panelContext.orderActiveTagIds;
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(
    () => new Set(initialTagIds),
  );
  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Raw BE rows by id. The drafts only carry the columns this form
  // renders, but the cargo-item PUT assigns every field it receives —
  // so a body built from a draft alone would null the rest. Kept as the
  // echo-back source for both save paths.
  const rawCargoItemById = useMemo(
    () => new Map((source?.cargoItems ?? []).map((item) => [item.id, item])),
    [source],
  );

  // Cargo-item draft updates — partial patch over the existing draft.
  const handleCargoItemChange = (index: number, patch: Partial<CargoItemDraft>) => {
    setCargoItems((prev) => {
      const next = prev.slice();
      const current = next[index];
      if (!current) return prev;
      next[index] = { ...current, ...patch };
      return next;
    });
  };

  // Dirty when any task-level field differs from its initial value,
  // any cargo-item draft differs from the row it was hydrated
  // from (deep compare against `initialCargoItems`), OR the
  // per-task tag selection changed (set-equality against the
  // hydrated initial). Tag toggles now round-trip via the
  // OrderTask service-tag join.
  const isCargoDirty = useMemo(() => {
    if (cargoItems.length !== initialCargoItems.length) return true;
    for (let i = 0; i < cargoItems.length; i++) {
      const a = cargoItems[i];
      const b = initialCargoItems[i];
      if (!a || !b) return true;
      if (
        a.identifier !== b.identifier ||
        a.seal !== b.seal ||
        a.tankStatus !== b.tankStatus ||
        a.capacity !== b.capacity ||
        a.weightKg !== b.weightKg ||
        a.volumeM3 !== b.volumeM3 ||
        a.loadingMetresM !== b.loadingMetresM ||
        a.description !== b.description ||
        a.quantity !== b.quantity ||
        !productLinesEqual(a.products, b.products)
      ) {
        return true;
      }
    }
    return false;
  }, [cargoItems, initialCargoItems]);
  // Tag-set dirty — comparing two `Set<string>`s requires a manual
  // round-trip (size match + every member of the initial set still
  // present in the current set). The form's initial set is the
  // hydrated `initialTagIds` (task's own if present, order's
  // fallback otherwise).
  const isTagsDirty = useMemo(() => {
    if (selectedTagIds.size !== initialTagIds.length) return true;
    for (const id of initialTagIds) {
      if (!selectedTagIds.has(id)) return true;
    }
    return false;
  }, [selectedTagIds, initialTagIds]);

  const isDirty =
    notes !== initialNotes ||
    plannedDate !== initialPlannedDate ||
    plannedTime !== initialPlannedTime ||
    timeMode !== initialTimeMode ||
    addressId !== initialAddressId ||
    taskReference !== initialTaskReference ||
    temperatureC !== initialTemperatureC ||
    // A unit-family delivery can be dirty either way: the allocation table
    // and the unit row above it are both editable and both save.
    (isDelivery ? isAllocationsDirty || (isUnitFamily && isCargoDirty) : isCargoDirty) ||
    isTagsDirty;

  const isBusy =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    updateCargoItemMutation.isPending ||
    createCargoItemMutation.isPending ||
    deleteCargoItemMutation.isPending;

  // `kind` is immutable on the BE for EXISTING items (per
  // `UpdateOrderTaskCargoItemRequest`), so the per-item PUTs don't
  // re-send it. But when an empty draft row gets filled in by the
  // operator and saved, the POST needs a kind — that's the
  // family-derived one.
  const cargoItemKind = useMemo(
    () => cargoItemKindFor(panelContext.cargoFamily),
    [panelContext.cargoFamily],
  );
  // Container / tank carry their unit row on EVERY task type — the barrel
  // being washed or inspected still has to be named. Pallet-family cargo
  // keeps to the handling legs. See `taskTypeOwnsCargoItems`.
  const ownsCargoItems = taskTypeOwnsCargoItems(
    panelContext.cargoFamily,
    source?.type ?? 'PickUp',
  );
  // A delivery leg of a unit family shows BOTH: the unit row (which barrel
  // arrived, in what state) above the allocation table (how much came off
  // it). They answer different questions and the operator needs both.
  const showUnitRowOnDelivery = isUnitFamily && isDelivery;
  // Reference shows for every type except the process events
  // (Stop / Instruction / Inspection). Temperature shows only when the
  // order's cargo type is temperature-controlled.
  const showReference = taskTypeSupportsReference(source?.type ?? 'PickUp');
  const showTaskTemperature = panelContext.temperatureSpec != null;

  // Combobox options for the location picker — same shape as the
  // create form so the dropdown reads identically.
  const locationOptions = useMemo<ComboboxOption[]>(
    () =>
      panelContext.addresses.map((a) => ({
        value: a.id,
        label: a.city ? `${a.name} — ${a.city}` : a.name,
      })),
    [panelContext.addresses],
  );

  // Resolve the currently-picked address for the map + helper line.
  const selectedAddress = useMemo(
    () => panelContext.addresses.find((a) => a.id === addressId) ?? null,
    [panelContext.addresses, addressId],
  );

  // Reconcile a delivery leg's sourced cargo items against the allocations:
  // upsert one delivery item per allocated collection source (qty + pro-rated
  // LM/weight, commodity copied from the collection), delete sourced items that
  // are no longer allocated. Returns the count of failed operations. Legacy
  // delivery items WITHOUT a source are left untouched.
  const reconcileDeliveryAllocations = async (): Promise<number> => {
    if (!source) return 0;
    let errors = 0;
    // Unit fields belong to the reconciler on a delivery, not to the draft
    // loop in `handleSave`: every cargo row on a delivery leg is one this
    // function owns, and a row it deletes (a cascade-created legacy row)
    // would make the draft loop PUT against an id that no longer exists.
    //
    // The drafts are hydrated from the task's rows at mount, so a Tank ID
    // the cascade wrote onto a legacy row is already sitting in the unit
    // inputs above the allocation table. Writing it onto the allocation
    // rows here is what carries it across the legacy delete below.
    const unitCount = cargoItemRowCountFor(
      panelContext.cargoFamily,
      panelContext.perTrailer,
    );
    // The unit a delivery row belongs to is the COLLECTION unit it draws from
    // — every product taken off one tank carries that one tank's ID.
    const unitFieldsFor = (coll: CollectionItemOption) => {
      if (!isUnitFamily) return null;
      const draft = unitDraftForRow(cargoItems, unitCount, coll.unitIndex);
      if (!draft) return null;
      return {
        identifier: draft.identifier.trim() || null,
        seal: draft.seal.trim() || null,
        sealStatus: draft.sealStatus === '' ? null : draft.sealStatus,
        tankStatus: draft.tankStatus === '' ? null : draft.tankStatus,
        capacity: draft.capacity,
      };
    };
    const existingBySource = new Map<string, OrderTaskCargoItem>();
    const legacyItems: OrderTaskCargoItem[] = [];
    const usedIndexes = new Set<number>();
    let maxIndex = 0;
    for (const item of source.cargoItems ?? []) {
      if (item.itemIndex > maxIndex) maxIndex = item.itemIndex;
      usedIndexes.add(item.itemIndex);
      const key = allocationKeyOf(item);
      if (key) existingBySource.set(key, item);
      else legacyItems.push(item);
    }
    const collById = new Map(panelContext.collectionItems.map((c) => [c.id, c]));
    const slotOf = (item: OrderTaskCargoItem) =>
      deliveryUnitSlot(item, panelContext.collectionItems, unitCount);
    const slotOfUnit = (unit: number) => (unitCount <= 1 ? 1 : Math.min(Math.max(unit, 1), unitCount));
    // A legacy / unit-only row is THAT unit's row: an allocation from unit 2
    // must not attach to the row holding unit 1's Tank ID.
    const takeLegacy = (slot: number) => {
      const i = legacyItems.findIndex((item) => slotOf(item) === slot);
      return i < 0 ? undefined : legacyItems.splice(i, 1)[0];
    };
    // Units with an allocated row on this delivery after the save.
    const coveredSlots = new Set<number>();

    for (const [collId, qty] of Object.entries(allocations)) {
      if (qty <= 0) continue;
      const coll = collById.get(collId);
      if (!coll) continue;
      const d = deriveDeliveryItem(coll, qty);
      // A tank / container drop-off saved before tanks could be allocated has
      // its unit row with no source. Its first allocation ATTACHES to that row
      // rather than replacing it — the row may carry POD, customs or notes the
      // allocation knows nothing about.
      const existing =
        existingBySource.get(collId) ?? (isUnitFamily ? takeLegacy(slotOfUnit(coll.unitIndex)) : undefined);
      const unit = unitFieldsFor(coll);
      coveredSlots.add(slotOfUnit(coll.unitIndex));
      try {
        if (existing) {
          const changed =
            (existing.sourceCollectionItemId ?? null) !== d.sourceCollectionItemId ||
            (existing.sourceProductLineId ?? null) !== d.sourceProductLineId ||
            (existing.quantity ?? null) !== d.quantity ||
            existing.loadingMetresM !== d.loadingMetresM ||
            (existing.weightKg ?? null) !== d.weightKg ||
            (existing.volumeM3 ?? null) !== d.volumeM3 ||
            existing.description !== d.description ||
            existing.quantityUnit !== d.quantityUnit ||
            // Unit fields are edited on this form too now, so an
            // identifier-only change still has to reach the BE.
            (unit != null &&
              ((existing.identifier ?? null) !== unit.identifier ||
                (existing.seal ?? null) !== unit.seal ||
                (existing.sealStatus ?? null) !== unit.sealStatus ||
                (existing.tankStatus ?? null) !== unit.tankStatus ||
                (existing.capacity ?? null) !== unit.capacity));
          if (changed) {
            await updateCargoItemMutation.mutateAsync({
              orderId: source.orderId,
              orderTaskId: source.id,
              // Allocations own quantity + the pro-rated metrics; the unit
              // fields come off the row above the table; everything else
              // (POD, customs, notes) belongs to other surfaces and is
              // echoed back untouched.
              body: cargoItemUpdateBody(existing, {
                sourceCollectionItemId: d.sourceCollectionItemId,
                sourceProductLineId: d.sourceProductLineId,
                description: d.description,
                quantity: d.quantity,
                quantityUnit: d.quantityUnit,
                loadingMetresM: d.loadingMetresM,
                weightKg: d.weightKg,
                volumeM3: d.volumeM3,
                ...(unit ?? {}),
              }),
            });
          }
        } else {
          maxIndex += 1;
          await createCargoItemMutation.mutateAsync({
            orderId: source.orderId,
            body: {
              orderTaskId: source.id,
              itemIndex: maxIndex,
              kind: d.kind,
              sourceCollectionItemId: d.sourceCollectionItemId,
              sourceProductLineId: d.sourceProductLineId,
              description: d.description,
              quantity: d.quantity,
              quantityUnit: d.quantityUnit,
              loadingMetresM: d.loadingMetresM,
              weightKg: d.weightKg,
              volumeM3: d.volumeM3,
              ...(unit ?? {}),
            },
          });
        }
      } catch (err) {
        errors += 1;
        console.error('Allocation save failed', err);
      }
    }

    // Deallocated sources → delete the delivery item.
    for (const [collId, item] of existingBySource) {
      if ((allocations[collId] ?? 0) > 0) continue;
      try {
        await deleteCargoItemMutation.mutateAsync({
          orderId: source.orderId,
          orderTaskId: source.id,
          cargoItemId: item.id,
        });
      } catch (err) {
        errors += 1;
        console.error('Allocation delete failed', err);
      }
    }

    // A unit with nothing allocated off it yet still has a Tank ID / BIC on
    // this leg — the operator typed it above the (empty) allocation table.
    // It lives on a unit-only row: the unit's legacy row when there is one,
    // else a new row numbered by its unit so it reads back into the same slot.
    // That row is what the first allocation off this unit later attaches to.
    if (isUnitFamily) {
      for (let slot = 1; slot <= unitCount; slot++) {
        if (coveredSlots.has(slot)) continue;
        const draft = unitDraftForRow(cargoItems, unitCount, slot);
        if (!draft) continue;
        const unit = {
          identifier: draft.identifier.trim() || null,
          seal: draft.seal.trim() || null,
          sealStatus: draft.sealStatus === '' ? null : draft.sealStatus,
          tankStatus: draft.tankStatus === '' ? null : draft.tankStatus,
          capacity: draft.capacity,
        };
        const own = takeLegacy(slot);
        try {
          if (own) {
            const changed =
              (own.identifier ?? null) !== unit.identifier ||
              (own.seal ?? null) !== unit.seal ||
              (own.sealStatus ?? null) !== unit.sealStatus ||
              (own.tankStatus ?? null) !== unit.tankStatus ||
              (own.capacity ?? null) !== unit.capacity;
            if (changed) {
              await updateCargoItemMutation.mutateAsync({
                orderId: source.orderId,
                orderTaskId: source.id,
                body: cargoItemUpdateBody(own, unit),
              });
            }
          } else if (
            unit.identifier ||
            unit.seal ||
            unit.sealStatus ||
            unit.tankStatus ||
            unit.capacity != null
          ) {
            const itemIndex = usedIndexes.has(slot) ? ++maxIndex : slot;
            usedIndexes.add(itemIndex);
            await createCargoItemMutation.mutateAsync({
              orderId: source.orderId,
              body: { orderTaskId: source.id, itemIndex, kind: cargoItemKind, ...unit },
            });
          }
        } catch (err) {
          errors += 1;
          console.error('Unit row save failed', err);
        }
      }
    }

    // Legacy free-entered delivery items (no source) — clear them once the
    // delivery is genuinely allocation-driven so the header total can't
    // double-count. Only when there's at least one allocation, so saving an
    // unrelated field on an untouched legacy delivery doesn't wipe its data.
    // The rows left here belong to units that ARE allocated (an uncovered
    // unit's row was taken above), so no unallocated unit loses its Tank ID.
    if (Object.keys(allocations).length > 0) {
      for (const item of legacyItems) {
        try {
          await deleteCargoItemMutation.mutateAsync({
            orderId: source.orderId,
            orderTaskId: source.id,
            cargoItemId: item.id,
          });
        } catch (err) {
          errors += 1;
          console.error('Legacy delivery item delete failed', err);
        }
      }
    }
    return errors;
  };

  /**
   * Pending cascade offer — the identifiers just saved plus the following
   * tasks they'd be copied onto. Non-null means the dialog is up, and the
   * form deliberately stays open behind it: the operator is still being
   * asked about the edit they just made.
   */
  const [cascade, setCascade] = useState<{
    identifiers: Map<number, string>;
    targets: CascadeTarget[];
  } | null>(null);

  const cascadeLabel = unitIdentifierLabel(panelContext.cargoFamily);

  /**
   * Copy the saved unit identifiers onto the following tasks. Writes are
   * sequential — each invalidates the same order's task list, and a burst
   * of parallel PUTs would have them racing each other's refetches.
   */
  const applyCascade = async (pending: {
    identifiers: Map<number, string>;
    targets: CascadeTarget[];
  }) => {
    if (!source) return;
    let errors = 0;
    let written = 0;
    for (const target of pending.targets) {
      for (const stamp of target.stamps) {
        try {
          await updateCargoItemMutation.mutateAsync({
            orderId: source.orderId,
            orderTaskId: target.taskId,
            body: cargoItemUpdateBody(stamp.item, { identifier: stamp.identifier }),
          });
          written += 1;
        } catch (err) {
          errors += 1;
          console.error('Identifier cascade update failed', err);
        }
      }
      for (const slot of target.missingSlots) {
        try {
          await createCargoItemMutation.mutateAsync({
            orderId: source.orderId,
            body: {
              orderTaskId: target.taskId,
              itemIndex: slot.itemIndex,
              kind: cargoItemKind,
              identifier: slot.identifier,
            },
          });
          written += 1;
        } catch (err) {
          errors += 1;
          console.error('Identifier cascade create failed', err);
        }
      }
    }
    setCascade(null);
    if (errors > 0) {
      showToast({
        type: 'error',
        message:
          written === 0
            ? `The ${cascadeLabel} could not be copied to the later tasks.`
            : `${cascadeLabel} copied to ${written} row(s), but ${errors} failed.`,
      });
    } else {
      showToast({
        type: 'success',
        message:
          pending.targets.length === 1
            ? `${cascadeLabel} copied to the next task.`
            : `${cascadeLabel} copied to ${pending.targets.length} later tasks.`,
      });
    }
    onClose();
  };

  const handleSave = async () => {
    if (!source) return;
    // Temperature range guard — only when the box is shown. Mirrors the
    // order-level + create-form violation toasts.
    if (showTaskTemperature) {
      const violation = temperatureRangeViolation(
        temperatureC,
        panelContext.temperatureSpec?.minTempC,
        panelContext.temperatureSpec?.maxTempC,
      );
      if (violation) {
        showToast({ type: 'error', message: violation });
        return;
      }
    }
    // Captured before any write: `initialCargoItems` is a memo over the
    // loaded task, so a refetch triggered by the save itself would re-seed
    // it and the diff would come back empty.
    // Deliveries used to be excluded because they had no editable identifier
    // to change — the unit row didn't render for them. Now it does, and a
    // Tank ID corrected on a DropOff belongs to the legs after it exactly as
    // one entered on the PickUp does.
    const cascadeIdentifiers = isUnitFamily
      ? changedUnitIdentifiers(cargoItems, initialCargoItems)
      : new Map<number, string>();

    try {
      // Step 1: the task-level PUT — sequence, address, planned
      // times, notes, etc. Required columns are echoed back
      // unchanged so the PUT doesn't accidentally null-out POD /
      // actuals / statusText (those live on surfaces that don't
      // exist yet).
      await updateMutation.mutateAsync({
        orderId: source.orderId,
        body: {
          taskId: source.id,
          sequence: source.sequence,
          addressId: addressId || source.addressId,
          state: source.state,
          notes: notes.trim() || null,
          plannedDate: plannedDate || null,
          plannedTime: timeMode === 'Exact' ? plannedTime || null : null,
          plannedDepartureTime: source.plannedDepartureTime ?? null,
          timeMode,
          actualDate: source.actualDate ?? null,
          actualArrivalTime: source.actualArrivalTime ?? null,
          actualDepartureTime: source.actualDepartureTime ?? null,
          statusText: source.statusText ?? null,
          // Per-stop reference — editable for every type except the
          // process events; echo null for those so the PUT doesn't carry
          // a stale value.
          taskReference: showReference ? taskReference.trim() || null : null,
          // Per-task temperature — only for temperature-controlled cargo;
          // null otherwise so non-temp cargo never persists a reading.
          temperatureC: showTaskTemperature ? temperatureC : null,
          podStatus: source.podStatus ?? null,
          podSignedAt: source.podSignedAt ?? null,
          podSignedBy: source.podSignedBy ?? null,
          // Per-task tag selection — diff-replaced server-side
          // against the task's existing join rows.
          serviceTagIds: Array.from(selectedTagIds),
        },
      });

      // Step 2: per-cargo-item round-trip. For each draft:
      //   - existing item (`initial.id` present): PUT if any field
      //     changed; skip if untouched.
      //   - empty slot (`initial.id` absent) that the operator
      //     filled in: POST a new cargo item bound to this task.
      //   - empty slot left empty: skip.
      // Delete (clear an existing row) isn't wired here — that
      // moves to the phase-3 cargo-item CRUD pass alongside add /
      // remove affordances.
      let cargoErrors = 0;
      // Delivery legs are allocation-driven; collection legs use the per-slot
      // draft loop below. The `!isDelivery` guard short-circuits the loop for
      // deliveries so the draft path is skipped entirely.
      // Delivery legs are entirely reconciler-driven — including the unit
      // fields, which it folds into the rows it writes. The draft loop stays
      // off for them: its rows are matched by the id they were hydrated
      // with, and the reconciler may have just deleted one.
      if (isDelivery) cargoErrors += await reconcileDeliveryAllocations();
      const runDraftLoop = ownsCargoItems && !isDelivery;
      for (let i = 0; runDraftLoop && i < cargoItems.length; i++) {
        const draft = cargoItems[i];
        const initial = initialCargoItems[i];
        if (!draft) continue;
        const draftHasData =
          draft.identifier.trim() !== '' ||
          draft.seal.trim() !== '' ||
          draft.sealStatus !== '' ||
          draft.tankStatus !== '' ||
          draft.capacity != null ||
          draft.weightKg != null ||
          draft.volumeM3 != null ||
          draft.description.trim() !== '' ||
          draft.quantity != null ||
          draft.products.length > 0;

        if (initial?.id) {
          // Existing row — PUT when changed.
          const changed =
            draft.identifier !== initial.identifier ||
            draft.seal !== initial.seal ||
            draft.sealStatus !== initial.sealStatus ||
            draft.tankStatus !== initial.tankStatus ||
            draft.capacity !== initial.capacity ||
            draft.weightKg !== initial.weightKg ||
            draft.volumeM3 !== initial.volumeM3 ||
            draft.loadingMetresM !== initial.loadingMetresM ||
            draft.description !== initial.description ||
            draft.quantity !== initial.quantity ||
            !productLinesEqual(draft.products, initial.products);
          if (!changed) continue;
          try {
            const raw = rawCargoItemById.get(initial.id);
            if (!raw) {
              // The draft claims a row id that isn't in the loaded task —
              // shouldn't happen (drafts are hydrated FROM those rows), and
              // guessing at a body here would blank real columns.
              cargoErrors += 1;
              console.error('Cargo item row not found for draft', initial.id);
              continue;
            }
            await updateCargoItemMutation.mutateAsync({
              orderId: source.orderId,
              orderTaskId: source.id,
              // Echoed off the raw row so columns this form never renders
              // (customs reference, clearance, POD, notes) survive the
              // whole-object PUT.
              body: cargoItemUpdateBody(raw, {
                // BE `itemIndex` is 1-based. Drafts hydrated by
                // `initialCargoItems` already carry the right
                // value; the `?? i + 1` fallback only fires for
                // legacy paths that didn't seed it.
                itemIndex: draft.itemIndex ?? initial.itemIndex ?? i + 1,
                identifier: draft.identifier.trim() || null,
                seal: draft.seal.trim() || null,
                sealStatus: draft.sealStatus === '' ? null : draft.sealStatus,
                tankStatus: draft.tankStatus === '' ? null : draft.tankStatus,
                capacity: draft.capacity,
                weightKg: draft.weightKg,
                volumeM3: draft.volumeM3,
                loadingMetresM: resolveLoadingMetres(draft, panelContext.palletVariants),
                description: draft.description.trim() || null,
                quantity: draft.quantity,
                quantityUnit:
                  panelContext.cargoFamily === 'pallet' && draft.quantity != null
                    ? 'pallet'
                    : null,
                products: toProductInputs(draft.products),
              }),
            });
          } catch (err) {
            cargoErrors += 1;
            console.error('Cargo item update failed', err);
          }
        } else if (draftHasData) {
          // Empty slot the operator filled in — POST a new item.
          // The save handler's outer task PUT has already echoed
          // the task's sequence / state etc.; new items get
          // attached to it via `orderTaskId`.
          try {
            await createCargoItemMutation.mutateAsync({
              orderId: source.orderId,
              body: {
                orderTaskId: source.id,
                // 1-based — see comment on the PUT branch above.
                itemIndex: draft.itemIndex ?? i + 1,
                kind: cargoItemKind,
                identifier: draft.identifier.trim() || null,
                seal: draft.seal.trim() || null,
                sealStatus: draft.sealStatus === '' ? null : draft.sealStatus,
                tankStatus: draft.tankStatus === '' ? null : draft.tankStatus,
                capacity: draft.capacity,
                weightKg: draft.weightKg,
                volumeM3: draft.volumeM3,
                loadingMetresM: resolveLoadingMetres(draft, panelContext.palletVariants),
                description: draft.description.trim() || null,
                quantity: draft.quantity,
                quantityUnit:
                  panelContext.cargoFamily === 'pallet' && draft.quantity != null
                    ? 'pallet'
                    : null,
                products: toProductInputs(draft.products),
              },
            });
          } catch (err) {
            cargoErrors += 1;
            console.error('Cargo item create failed', err);
          }
        }
        // else: empty slot left empty — nothing to persist.
      }

      if (cargoErrors > 0) {
        showToast({
          type: 'error',
          message:
            cargoErrors === 1
              ? 'Task saved, but one cargo row failed to update.'
              : `Task saved, but ${cargoErrors} cargo rows failed to update.`,
        });
        return;
      }

      // A container or tank is one physical unit travelling the whole job,
      // so an identifier entered here almost always belongs to the legs
      // that follow. Offered downstream only — an earlier leg can have
      // legitimately carried a different box.
      const targets = cascadeTargets({
        tasks: panelContext.siblingTasks,
        family: panelContext.cargoFamily,
        fromTaskId: source.id,
        fromSequence: source.sequence,
        unitCount: cargoItemRowCountFor(panelContext.cargoFamily, panelContext.perTrailer),
        identifiers: cascadeIdentifiers,
      });
      showToast({ type: 'success', message: 'Task updated.' });
      if (targets.length > 0) {
        // Hold the form open behind the dialog — the operator is being
        // asked about the edit they just made, not a new one.
        setCascade({ identifiers: cascadeIdentifiers, targets });
        return;
      }
      onClose();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update task.',
      });
    }
  };

  const confirmDelete = async () => {
    if (!source) return;
    try {
      await deleteMutation.mutateAsync({ orderId: source.orderId, taskId: source.id });
      showToast({ type: 'success', message: 'Task deleted.' });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete task.',
      });
    } finally {
      setPendingDelete(false);
    }
  };

  // `timeMode` UI binding bridges BE `'Any'` → display `'Undefined'`.
  // Pill picker (existing component) speaks the display vocab; the
  // controlled state above is in the wire vocab so save bodies are
  // already correct.
  const displayTimeMode: TimeMode = timeMode === 'Any' ? 'Undefined' : timeMode;
  const setDisplayTimeMode = (next: TimeMode) => {
    setTimeMode(next === 'Undefined' ? 'Any' : next);
  };

  // Operator-facing label for the task type (read-only — type is
  // immutable on the BE once a task is created).
  const typeLabel = source ? taskTypeLabel(source.type) : (TASK_TYPE_CONFIG[task.type]?.label ?? task.type);

  // No source means we're rendering against a mock row (the
  // gated-off `CargoItemsPanel` / `CargoSummaryView` paths). Bail
  // out with a thin read-only view so those paths still render
  // without crashing on the BE-bound save handlers.
  if (!source) {
    return (
      <div className="rounded-md border border-purple-200 bg-purple-50/40 p-3 text-xs italic text-slate-500">
        No backing data for this task.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
      {/* Edit task box — mirrors the layout of `TaskCreateForm` so
          create and edit read identically. Contains every form
          input + the action bar; the map is the only sibling
          outside the box. */}
      <div className="space-y-3 rounded-md border border-purple-200 bg-purple-50/40 p-3">
        <h3 className="text-sm font-semibold text-slate-900">
          {isPickup ? 'Pickup details' : 'Delivery details'}
        </h3>

        {/* Row 1 — Type (read-only, immutable) · Date + Time
            stacked inside col 2 · Time mode pills in col 3. */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <TextInput label="Type" value={typeLabel} readOnly />
          <div className="grid grid-cols-2 gap-2">
            <div className={timeMode === 'Exact' ? '' : 'col-span-2'}>
              <TextInput
                label="Planned date"
                type="date"
                value={plannedDate}
                disabled={isBusy}
                onChange={(e) => setPlannedDate(e.target.value)}
              />
            </div>
            {timeMode === 'Exact' && (
              <QuarterHourTimeInput
                label="Planned time"
                value={plannedTime}
                disabled={isBusy}
                onChange={setPlannedTime}
              />
            )}
          </div>
          <div className="flex min-h-9 flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Time mode
            </span>
            <TimeModePicker value={displayTimeMode} onChange={setDisplayTimeMode} />
          </div>
        </div>

        {/* Row 2 — Location 50/50 with Address. Operator can re-pick
            the location; the BE accepts a different `addressId` on
            the task PUT. Address text wraps inside col 2. */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* "+" makes a Location on the spot and picks it — see TaskCreateForm. */}
          <div className="flex items-start gap-1.5">
            <div className="min-w-0 flex-1">
              <Combobox
                label={isPickup ? 'Pickup location *' : 'Delivery location *'}
                value={addressId}
                options={locationOptions}
                onChange={setAddressId}
                placeholder={
                  locationOptions.length === 0
                    ? 'No locations yet — add one with +'
                    : 'Start typing a location or city…'
                }
                allowClear
              />
            </div>
            <NewLocationButton
              organisationId={panelContext.organisationId}
              onCreated={setAddressId}
              disabled={isBusy}
            />
          </div>
          <div className="flex min-h-9 items-center">
            {selectedAddress?.fullAddress ? (
              <p
                className="text-sm italic leading-snug text-slate-600"
                title={selectedAddress.fullAddress}
              >
                {selectedAddress.fullAddress}
              </p>
            ) : (
              <p className="text-xs italic text-slate-300">
                Address shows here once a location is picked
              </p>
            )}
          </div>
        </div>

        {/* Reference + per-task temperature. Reference hides for the
            process-event types (Stop / Instruction / Inspection);
            temperature only shows for temperature-controlled cargo. */}
        {(showReference || showTaskTemperature) && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {showReference && (
              <TextInput
                label="Reference"
                name="task-reference"
                autoComplete="off"
                value={taskReference}
                disabled={isBusy}
                maxLength={64}
                onChange={(e) => setTaskReference(e.target.value)}
              />
            )}
            {showTaskTemperature && (
              <InputWithSpecHint
                spec={formatTempSpecRange(
                  panelContext.temperatureSpec?.minTempC,
                  panelContext.temperatureSpec?.maxTempC,
                  panelContext.temperatureSpec?.maxTransitDays,
                )}
              >
                <NumberFieldInput
                  label="Temp (°C)"
                  value={temperatureC}
                  disabled={isBusy}
                  onChange={setTemperatureC}
                  allowNegative
                />
              </InputWithSpecHint>
            )}
          </div>
        )}

        {/* Cargo items. The row count is driven by `perTrailer` so the
            operator sees the same slot shape as the create form; existing
            items hydrate their slot from the BE, empty slots stay blank
            and become new cargo-item POSTs at save time if the operator
            fills them in.

            Pallet-family cargo appears on the handling legs only — a Stop
            has no quantity to record. Container / tank appear everywhere,
            because the unit itself is what the task is about.

            On a unit-family DELIVERY both sections show: the unit row says
            which barrel arrived and in what state, the allocation table
            says how much came off it. */}
        {ownsCargoItems && (
          <>
            {(!isDelivery || showUnitRowOnDelivery) && (
              <CargoItemInputsSection
                family={panelContext.cargoFamily}
                taskType={source?.type ?? 'PickUp'}
                drafts={cargoItems}
                disabled={isBusy}
                palletVariants={panelContext.palletVariants}
                productOptions={panelContext.productOptions}
                multiProduct={panelContext.multiProduct}
                onChange={handleCargoItemChange}
              />
            )}
            {isDelivery && (
              <AllocationTable
                items={panelContext.collectionItems}
                availableById={availableById}
                value={allocations}
                onChange={setAllocations}
                disabled={isBusy}
              />
            )}
          </>
        )}

        {/* Service tag picker. All tags applicable to this order's
            cargo are rendered as toggleable chips; the order's
            active set seeds the per-task selection so the operator
            sees the inherited defaults and can adjust. */}
        {panelContext.serviceTagsCatalogue.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Tags
            </span>
            {panelContext.serviceTagsCatalogue.map((tag) => {
              const isActive = selectedTagIds.has(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  disabled={isBusy}
                  aria-pressed={isActive}
                  title={
                    isActive
                      ? `Click to remove ${tag.name} from this task`
                      : `Click to add ${tag.name} to this task`
                  }
                  className={
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50 ' +
                    (isActive
                      ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      : 'border border-slate-300 bg-white text-slate-500 hover:border-orange-300 hover:text-orange-600')
                  }
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Notes — last in the visual flow, same as create. */}
        <TextAreaInput
          label="Notes"
          value={notes}
          disabled={isBusy}
          onChange={(e) => setNotes(e.target.value)}
        />

        {/* Trash on the left · Cancel / Save on the right. All
            inside the task box, matching the create form's
            footer layout. */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label="Delete task"
            onClick={() => setPendingDelete(true)}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Cancel changes"
              onClick={onClose}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Save task"
              onClick={handleSave}
              disabled={!isDirty || isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Map preview — sibling of the task box, NOT inside it. Pin
          follows the picked address as the operator changes the
          location combobox. */}
      <TaskLocationMap selected={selectedAddress} />

      <ConfirmDialog
        open={pendingDelete}
        tone="danger"
        title="Delete task"
        message={
          source
            ? `Delete ${task.title}${task.subtitle ? ', ' + task.subtitle : ''}?`
            : null
        }
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(false)}
        busy={deleteMutation.isPending}
      />

      {/* Cascade down. The box or barrel entered here is the same physical
          unit at every stop that follows, so offering to copy it saves the
          operator retyping the code once per leg. Offer, not
          automatic: a later leg can be a different unit (a swap), and only
          the operator knows. */}
      <ConfirmDialog
        open={cascade !== null}
        title={`Copy the ${cascadeLabel} to the later tasks?`}
        message={
          cascade && (
            <span className="block space-y-2">
              <span className="block">
                {cascade.identifiers.size === 1
                  ? `${[...cascade.identifiers.values()][0]} will be set on:`
                  : `The ${cascadeLabel}s entered here will be set on:`}
              </span>
              <span className="block space-y-0.5 pl-1">
                {cascade.targets.map((t) => (
                  <span key={t.taskId} className="block">
                    {t.task._source ? taskTypeLabel(t.task._source.type) : (TASK_TYPE_CONFIG[t.task.type]?.label ?? t.task.type)} · {t.task.title}
                  </span>
                ))}
              </span>
              <span className="block text-slate-500">
                Tasks before this one are left as they are.
              </span>
            </span>
          )
        }
        confirmLabel={
          cascade?.targets.length === 1
            ? 'Copy to the next task'
            : `Copy to ${cascade?.targets.length ?? 0} tasks`
        }
        cancelLabel="This task only"
        busy={updateCargoItemMutation.isPending || createCargoItemMutation.isPending}
        onConfirm={() => {
          if (cascade) void applyCascade(cascade);
        }}
        onCancel={() => {
          // Declining is a real answer: the task itself is already saved,
          // so just close as a normal save would have.
          setCascade(null);
          onClose();
        }}
      />
    </div>
  );
}
